'use client'

import React, { createContext, useContext, useEffect, useState, useRef } from 'react'
import { createClientSupabaseClient } from '@/supabase/client'
import { User } from '@supabase/supabase-js'
import { useToast } from '@/hooks/use-toast'

// 定义持久化状态的接口
interface PersistentState {
  inputContent?: string;
  description?: string;
  activeScenario?: string;
  activeTarget?: string;
  activeTone?: string;
}

// 定义用户信息持久化接口
interface PersistentUserInfo {
  id: string;
  email?: string;
  user_metadata?: any;
  app_metadata?: any;
  created_at?: string;
  updated_at?: string;
}

// 定义用户订阅接口
interface UserSubscription {
  plan: string;
  status: string;
  email?: string;
  price_id?: string;
  current_period_start?: string;
  current_period_end?: string;
  quantity?: number;
  ended_at?: string | null;
  cancel_at_period_end?: boolean; // 新增：标记是否已安排到期取消
  pending_plan?: string | null;
  pending_price_id?: string | null;
  pending_apply_at?: string | null;
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  userSubscription: UserSubscription | null
  refreshUserSubscription: () => Promise<void>
  signInWithGoogle: () => Promise<void>
  signInWithGithub: () => Promise<void>
  signOut: () => Promise<void>
  // 状态持久化相关函数
  savePersistentState: (state: PersistentState) => void;
  getPersistentState: () => PersistentState | null;
  clearPersistentState: () => void;
}

const AUTH_STATE_KEY = 'communicate2win_persistent_state';
const USER_INFO_KEY = 'communicate2win_user_info';
const USER_SUBSCRIPTION_KEY = 'communicate2win_user_subscription';

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [userSubscription, setUserSubscription] = useState<UserSubscription | null>(null)
  const [isInitialized, setIsInitialized] = useState<boolean>(false)
  const { toast } = useToast()
  const supabase = createClientSupabaseClient()
  
  // 使用ref来保存最新的用户状态，避免闭包问题
  const userRef = useRef<User | null>(null)
  
  // 更新ref当user状态变化时
  useEffect(() => {
    userRef.current = user
  }, [user])

  // 调试函数
  const setLoadingWithLog = (loading: boolean, reason: string) => {
    // console.log(`🔄 Setting isLoading to ${loading} - Reason: ${reason}`)
    setIsLoading(loading)
    // console.log('isLoading: ', isLoading)
  }

  // 保存用户信息到本地存储
  const saveUserToLocal = (user: User) => {
    if (typeof window !== 'undefined') {
      const userInfo: PersistentUserInfo = {
        id: user.id,
        email: user.email,
        user_metadata: user.user_metadata,
        app_metadata: user.app_metadata,
        created_at: user.created_at,
        updated_at: user.updated_at
      }
      localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo))
    }
  }

  // 从本地存储恢复用户信息
  const loadUserFromLocal = (): User | null => {
    if (typeof window !== 'undefined') {
      const savedUserInfo = localStorage.getItem(USER_INFO_KEY)
      if (savedUserInfo) {
        try {
          const userInfo: PersistentUserInfo = JSON.parse(savedUserInfo)
          // 构造User对象（只包含必要字段）
          return {
            id: userInfo.id,
            email: userInfo.email,
            user_metadata: userInfo.user_metadata || {},
            app_metadata: userInfo.app_metadata || {},
            created_at: userInfo.created_at || new Date().toISOString(),
            updated_at: userInfo.updated_at || new Date().toISOString(),
            aud: 'authenticated',
            role: 'authenticated'
          } as User
        } catch (error) {
          console.error('Error parsing saved user info:', error)
          localStorage.removeItem(USER_INFO_KEY)
        }
      }
    }
    return null
  }

  // 保存订阅信息到本地存储
  const saveSubscriptionToLocal = (subscription: UserSubscription) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(USER_SUBSCRIPTION_KEY, JSON.stringify(subscription))
    }
  }

  // 从本地存储恢复订阅信息
  const loadSubscriptionFromLocal = (): UserSubscription | null => {
    if (typeof window !== 'undefined') {
      const savedSubscription = localStorage.getItem(USER_SUBSCRIPTION_KEY)
      if (savedSubscription) {
        try {
          return JSON.parse(savedSubscription)
        } catch (error) {
          console.error('Error parsing saved subscription info:', error)
          localStorage.removeItem(USER_SUBSCRIPTION_KEY)
        }
      }
    }
    return null
  }

  // 清除本地用户信息
  const clearUserFromLocal = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(USER_INFO_KEY)
      localStorage.removeItem(USER_SUBSCRIPTION_KEY)
    }
  }

  // 获取用户订阅信息 - 使用cookie认证API
  const fetchUserSubscription = async (userId: string): Promise<void> => {
    try {
      // 添加超时处理，防止长时间等待
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Subscription fetch timeout')), 10000) // 10秒超时
      })
      
      // 使用cookie认证的API端点
      const fetchPromise = fetch('/api/subscription', {
        method: 'GET',
        credentials: 'include', // 包含cookies
        headers: {
          'Content-Type': 'application/json',
        },
      })

      const response = await Promise.race([fetchPromise, timeoutPromise])

      if (!response.ok) {
        if (response.status === 401) {
          console.warn('User not authenticated for subscription query')
          const defaultSubscription = { plan: 'free', status: 'active', quantity: 10 }
          setUserSubscription(defaultSubscription)
          saveSubscriptionToLocal(defaultSubscription)
          return
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      
      if (result.error) {
        console.error('fetchUserSubscription()-> result.error:', result.error)
        //const defaultSubscription = { plan: 'free', status: 'active', quantity: 10 }
        //setUserSubscription(defaultSubscription)
        //saveSubscriptionToLocal(defaultSubscription)
        return
      }

      // 处理API返回的数据格式
      const subscriptionData = result.data
      if (subscriptionData) {
        // 确保包含quantity字段，如果没有则根据plan设置默认值
        const subscription = {
          plan: subscriptionData.plan || 'free',
          status: subscriptionData.status || 'active',
          email: subscriptionData.email,
          price_id: subscriptionData.price_id,
          current_period_start: subscriptionData.current_period_start,
          current_period_end: subscriptionData.current_period_end,
          quantity: subscriptionData.quantity || (subscriptionData.plan === 'free' ? 10 : subscriptionData.plan === 'pro' ? 200 : 999999),
          ended_at: subscriptionData.ended_at,
          cancel_at_period_end: subscriptionData.cancel_at_period_end ?? false,
          pending_plan: subscriptionData.pending_plan ?? null,
          pending_price_id: subscriptionData.pending_price_id ?? null,
          pending_apply_at: subscriptionData.pending_apply_at ?? null,
        }

        // 调试输出：确认是否拿到 cancel_at_period_end
        if (typeof window !== 'undefined') {
          console.debug('[fetchUserSubscription] subscription fetched', subscription)
        }
        
        setUserSubscription(subscription)
        saveSubscriptionToLocal(subscription)
      } else {
        console.error('fetchUserSubscription()-> No subscriptionData！')
        // 如果没有数据，设置默认免费计划
        //const defaultSubscription = { plan: 'free', status: 'active', quantity: 10 }
        //setUserSubscription(defaultSubscription)
        //saveSubscriptionToLocal(defaultSubscription)
      }
    } catch (error) {
      console.error('fetchUserSubscription()-> Error:', error)
      // 出错时设置默认的免费计划
      //const defaultSubscription = { plan: 'free', status: 'active', quantity: 10 }
      //setUserSubscription(defaultSubscription)
      //saveSubscriptionToLocal(defaultSubscription)
    }
  }

  const refreshUserSubscription = async () => {
    if (user?.id) {
      await fetchUserSubscription(user.id)
    }
  }

  // 初始化用户状态
  const initializeAuth = async () => {
    try {
      // 首先尝试从本地存储恢复用户信息
      const localUser = loadUserFromLocal()
      const localSubscription = loadSubscriptionFromLocal()
      
      if (localUser) {
        // console.log('Found local user info, setting user immediately')
        setUser(localUser)
        if (localSubscription) {
          setUserSubscription(localSubscription)
        }
        // 不设置loading状态，直接显示用户头像
      }
      
      // 然后验证session（在后台进行）
      const { data: { session }, error } = await supabase.auth.getSession()
      
      if (error) {
        console.error('Error getting session:', error)
        // 如果session验证失败，清除本地存储的用户信息
        if (localUser) {
          setUser(null)
          setUserSubscription(null)
          clearUserFromLocal()
        }
        return
      }

      if (session?.user) {
        // console.log('Session validated for user:', session.user.id)
        // 更新用户信息（可能有变化）
        setUser(session.user)
        saveUserToLocal(session.user)
        
        // 获取最新的订阅信息
        await fetchUserSubscription(session.user.id)
      } else {
        // console.log('No valid session found')
        // 如果没有有效session，清除本地存储
        if (localUser) {
          setUser(null)
          setUserSubscription(null)
          clearUserFromLocal()
        }
      }
    } catch (error) {
      console.error('Error initializing auth:', error)
      // 出错时清除所有状态
      setUser(null)
      setUserSubscription(null)
      clearUserFromLocal()
    } finally {
      setIsInitialized(true)
      // 初始化完成后不设置loading为false，因为我们没有设置为true
    }
  }

  useEffect(() => {
    // 初始化认证状态
    initializeAuth()
    
    // 监听认证状态变化
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // console.log('Auth state changed:', event, session?.user?.id)
        
        // 只在初始化完成后处理状态变化
        if (!isInitialized && event !== 'INITIAL_SESSION') {
          return
        }
        
        // 需要设置loading状态的事件
        const shouldSetLoading = [
          'SIGNED_IN',
          'SIGNED_OUT',
          'TOKEN_REFRESHED'
        ].includes(event)
        
        if (shouldSetLoading) {
          setLoadingWithLog(true, `Processing auth event: ${event}`)
        }
        
        try {
          // 更新用户状态
          setUser(session?.user ?? null)
          
          // 处理用户信息本地存储
          if (session?.user) {
            saveUserToLocal(session.user)
            // console.log('Fetching subscription for user:', session.user.id)
            await fetchUserSubscription(session.user.id)
          } else {
            // console.log('No user found, clearing subscription and local storage')
            setUserSubscription(null)
            clearUserFromLocal()
          }
        } catch (error) {
          console.error('Error in auth state change:', error)
          // 确保即使出错也要清除loading状态
          setUser(session?.user ?? null)
          if (session?.user) {
            saveUserToLocal(session.user)
            const defaultSubscription = { plan: 'free', status: 'active', quantity: 10 }
            setUserSubscription(defaultSubscription)
            saveSubscriptionToLocal(defaultSubscription)
          } else {
            setUserSubscription(null)
            clearUserFromLocal()
          }
        } finally {
          // 只有在设置了loading状态时才重置它
          if (shouldSetLoading) {
            setLoadingWithLog(false, `Auth event processed: ${event}`)
          }
        }
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [isInitialized])

  // 暂时禁用页面可见性监听器来调试问题
  // TODO: 重新启用页面可见性监听器
  /*
  useEffect(() => {
    const handleVisibilityChange = async () => {
      // 当页面重新变为可见时，检查并恢复session
      if (document.visibilityState === 'visible') {
        console.log('🔍 页面重新可见，检查session状态')
        
        try {
          // 获取当前session
          const { data: { session }, error } = await supabase.auth.getSession()
          
          // 获取当前的用户状态（使用ref获取最新状态，避免闭包问题）
          const currentUser = userRef.current
          
          console.log('📊 Session检查结果:', {
            hasSession: !!session,
            hasAccessToken: !!session?.access_token,
            hasCurrentUser: !!currentUser,
            sessionUserId: session?.user?.id,
            currentUserId: currentUser?.id
          })
          
          // 如果没有session但本地有用户信息，尝试刷新session
          if (!session && currentUser) {
            console.log('🔄 检测到session丢失，尝试刷新session')
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession()
            
            if (refreshError) {
              console.error('❌ Session刷新失败:', refreshError)
              // 如果刷新失败，清除本地用户信息
              setUser(null)
              setUserSubscription(null)
              clearUserFromLocal()
            } else if (refreshData.session) {
              console.log('✅ Session刷新成功')
              // 更新用户状态
              setUser(refreshData.session.user)
              saveUserToLocal(refreshData.session.user)
              await fetchUserSubscription(refreshData.session.user.id)
            }
          }
          
          // 如果有session但用户状态不匹配，更新用户状态
          if (session?.user && (!currentUser || currentUser.id !== session.user.id)) {
            console.log('🔄 更新用户状态')
            setUser(session.user)
            saveUserToLocal(session.user)
            await fetchUserSubscription(session.user.id)
          }
          
          // 如果session和用户状态都正常，记录状态
          if (session?.user && currentUser && session.user.id === currentUser.id) {
            console.log('✅ Session状态正常，无需更新')
          }
          
        } catch (error) {
          console.error('💥 检查session状态时出错:', error)
        }
      }
    }

    // 只在组件挂载时添加一次页面可见性监听器
    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    // 清理函数
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, []) // 移除依赖项，只在组件挂载时添加一次监听器
  */

  // 保存状态到localStorage
  const savePersistentState = (state: PersistentState) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(AUTH_STATE_KEY, JSON.stringify(state));
    }
  };

  // 从localStorage获取状态
  const getPersistentState = (): PersistentState | null => {
    if (typeof window !== 'undefined') {
      const savedState = localStorage.getItem(AUTH_STATE_KEY);
      return savedState ? JSON.parse(savedState) : null;
    }
    return null;
  };

  // 清除保存的状态
  const clearPersistentState = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(AUTH_STATE_KEY);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setLoadingWithLog(true, 'Starting Google sign in')
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })

      if (error) {
        throw error
      }
    } catch (error: any) {
      setLoadingWithLog(false, 'Google sign in failed')
      toast({
        title: "Authentication Error",
        description: error.message || "Failed to sign in with Google",
        variant: "destructive"
      })
    }
  }

  const signInWithGithub = async () => {
    try {
      setLoadingWithLog(true, 'Starting GitHub sign in')
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })

      if (error) {
        throw error
      }
    } catch (error: any) {
      setLoadingWithLog(false, 'GitHub sign in failed')
      toast({
        title: "Authentication Error",
        description: error.message || "Failed to sign in with GitHub",
        variant: "destructive"
      })
    }
  }

    // 服务端登出方法
  const performServerSignOut = async () => {
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Server logout timeout')), 8000) // 8秒超时
    })
    
    try {
      // console.log('🌐 发送服务端登出请求...')
      const response = await Promise.race([
        fetch('/api/auth/signout', {
          method: 'POST',
          credentials: 'include', // 包含cookies
          headers: {
            'Content-Type': 'application/json',
          },
        }),
        timeoutPromise
      ]) as Response

      // console.log('📡 服务端登出响应状态:', response.status)
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Server logout failed!')
      }

      // console.log('✅ 服务端登出成功')
    } catch (backendError: any) {
      console.error('❌ Server logout failed:', backendError)
      // 即使服务端登出失败，也不抛出错误，让调用者继续清除本地状态
    }
  }

  // 前端登出方法
  const performFrontendSignOut = async () => {
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Front end logout timeout')), 5000) // 5秒超时
    })
    
    try {
      // console.log('🔄 开始前端登出流程...')
      
      // 在登出前更新用户订阅状态
      if (user) {
        // console.log('📝 更新用户订阅状态...')
        try {
          await Promise.race([
            supabase
              .from('user_subscriptions')
              .update({ status: 'logout' })
              .eq('user_id', user.id),
            timeoutPromise
          ])
          // console.log('✅ 订阅状态更新成功')
        } catch (updateError) {
          console.error('❌ Failed to update subscription status:', updateError)
        }
      }
      
      // console.log('🔄 执行 supabase.auth.signOut()...')
      await Promise.race([
        supabase.auth.signOut(),
        timeoutPromise
      ])
      // console.log('✅ 前端登出成功')
    } catch (frontendError: any) {
      console.error('❌ Front end logout failed, switch to server-side logout:', frontendError)
      await performServerSignOut()
    }
  }

  const signOut = async () => {
    // console.log('🚀 signOut 函数被调用')
    try {
      setLoadingWithLog(true, 'Starting sign out')
      // console.log('🔄 设置loading状态为true')
      
      // 直接使用服务端登出，避免 IndexedDB 挂起问题
      // console.log('🔄 使用服务端登出避免标签页切换问题')
      await performServerSignOut()
      
      // 无论登出是否成功，都清除本地状态
      // console.log('🧹 清除本地状态...')
      setUser(null)
      setUserSubscription(null)
      clearUserFromLocal()
      clearPersistentState()
      // console.log('✅ 本地状态清除完成')
      
      toast({
        title: "Signed Out",
        description: "You have been successfully signed out",
      })
    } catch (error: any) {
      console.error('💥 An error occurred during the logout process:', error)
      // 即使出现错误，也清除本地状态，防止用户卡在登录状态
      setUser(null)
      setUserSubscription(null)
      clearUserFromLocal()
      clearPersistentState()
      
      toast({
        title: "Sign Out Error",
        description: error.message || "Failed to sign out, but local session has been cleared",
        variant: "destructive"
      })
    } finally {
      // console.log('🏁 登出流程结束，设置loading为false')
      setLoadingWithLog(false, 'Sign out complete')
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        userSubscription,
        refreshUserSubscription,
        signInWithGoogle,
        signInWithGithub,
        signOut,
        // 状态持久化相关函数
        savePersistentState,
        getPersistentState,
        clearPersistentState
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
} 